import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { StyleSheet, View, Pressable, Platform, Animated } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import BannerCountdown from '@/components/Banner/BannerCountdown';
import { useModalStore } from '@/store';
import { isDarkTheme, getBannerAccent } from '@/constants/Colors';
import { BORDER_RADIUS } from '@/constants/config';
import { trackEvent } from '@/constants/utils/analytics';
import { executeBannerCta } from '@/lib/bannerActions';
import { bannerFallbackIcon, bannerSurface, webInteractive, withAlpha } from '@/components/Banner/bannerChrome';
import { shouldShowCtaButton } from '@/lib/bannerPlacementLogic';

const createStyles = (colors, isDark, accent, isCompact) => {
  const surface = bannerSurface(colors, isDark, accent);

  return StyleSheet.create({
    container: {
      ...surface,
      position: 'relative',
      padding: isCompact ? 12 : 14,
      paddingLeft: isCompact ? 14 : 16,
      gap: isCompact ? 10 : 12,
    },
    accentRail: {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: 3,
      backgroundColor: accent,
    },
    topRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 10,
    },
    headlineContainer: {
      flex: 1,
      gap: 4,
      minWidth: 0,
    },
    titleRow: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 10,
      minWidth: 0,
    },
    iconWell: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: withAlpha(accent, isDark ? 0.22 : 0.14),
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      marginTop: 1,
    },
    badge: {
      paddingHorizontal: 7,
      paddingVertical: 2,
      borderRadius: BORDER_RADIUS.round,
      backgroundColor: withAlpha(accent, isDark ? 0.22 : 0.12),
      alignSelf: 'flex-start',
    },
    headline: {
      lineHeight: 20,
    },
    subtitle: {
      lineHeight: 18,
      marginTop: 1,
    },
    dismissButton: {
      width: 28,
      height: 28,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      ...webInteractive,
    },
    dismissButtonHover: {
      backgroundColor: colors.solid_color_tertiary,
    },
    ctaBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      height: 32,
      minHeight: 32,
      paddingHorizontal: 12,
      borderRadius: BORDER_RADIUS.small + 2,
      backgroundColor: withAlpha(accent, isDark ? 0.22 : 0.12),
      borderWidth: 1,
      borderColor: withAlpha(accent, isDark ? 0.28 : 0.16),
      ...webInteractive,
    },
    ctaBtnHover: {
      backgroundColor: withAlpha(accent, isDark ? 0.32 : 0.2),
    },
    bottomRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
      flexWrap: 'wrap',
    },
    metaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      flexWrap: 'wrap',
      flexShrink: 1,
    },
    promoCodePill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: BORDER_RADIUS.small + 2,
      backgroundColor: withAlpha(accent, isDark ? 0.16 : 0.08),
      borderWidth: 1,
      borderColor: withAlpha(accent, isDark ? 0.32 : 0.2),
      borderStyle: 'dashed',
      ...webInteractive,
    },
    promoCodePillHover: {
      backgroundColor: withAlpha(accent, isDark ? 0.26 : 0.14),
      borderColor: withAlpha(accent, isDark ? 0.45 : 0.32),
    },
    countdownRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 8,
      paddingVertical: 5,
      borderRadius: BORDER_RADIUS.round,
      backgroundColor: colors.solid_color_tertiary,
    },
  });
};

function PromoBanner({
  banner,
  onMarkSeen,
  onCtaClick,
  onDismiss,
  isCompact = false,
  style,
}) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const router = useRouter();
  const { openModal } = useModalStore(state => state.actions);
  const hasFiredSeen = useRef(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(8)).current;

  const {
    id,
    contentType,
    content,
    placement,
    isDismissible,
    countdown,
    promoCode,
  } = banner;

  const accent = useMemo(
    () => getBannerAccent(contentType, isDark, colors),
    [contentType, isDark, colors],
  );
  const styles = useMemo(
    () => createStyles(colors, isDark, accent, isCompact),
    [colors, isDark, accent, isCompact],
  );
  const iconName = content.icon || bannerFallbackIcon(contentType);

  useEffect(() => {
    if (!hasFiredSeen.current) {
      hasFiredSeen.current = true;
      onMarkSeen?.(id);
    }
  }, [id, onMarkSeen]);

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 1, duration: 220, useNativeDriver: true }),
      Animated.spring(translateY, { toValue: 0, tension: 65, friction: 11, useNativeDriver: true }),
    ]).start();
  }, []);

  const handleCTA = useCallback(() => {
    trackEvent('conversion', 'banner_cta_clicked', placement, id);
    onCtaClick?.(id);
    executeBannerCta(banner, { router, openModal });
  }, [banner, placement, id, router, openModal, onCtaClick]);

  const handleDismiss = useCallback(() => {
    trackEvent('engagement', 'banner_dismissed', placement, id);
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 0, duration: 180, useNativeDriver: true }),
      Animated.timing(translateY, { toValue: -8, duration: 180, useNativeDriver: true }),
    ]).start(() => {
      onDismiss?.(id);
    });
  }, [id, placement, onDismiss, fadeAnim, translateY]);

  const handleCountdownExpired = useCallback(() => {
    onDismiss?.(id);
  }, [id, onDismiss]);

  const hasPromoCode = promoCode?.code;
  const hasCountdown = countdown?.enabled && countdown?.endsAt;
  const hasCTA = shouldShowCtaButton(banner);

  return (
    <Animated.View style={[styles.container, style, { opacity: fadeAnim, transform: [{ translateY }] }]}>
      <View style={styles.accentRail} />
      <View style={styles.topRow}>
        <View style={styles.titleRow}>
          <View style={styles.iconWell}>
            <Icon name={iconName} size={13} color={accent} solid />
          </View>
          <View style={styles.headlineContainer}>
            {!!content.badgeText && (
              <View style={styles.badge}>
                <Text size="xxs" weight="bold" style={{ color: accent, letterSpacing: 0.4 }}>
                  {content.badgeText}
                </Text>
              </View>
            )}
            {!!content.headline && (
              <Text size="s" weight="bold" color="primary" style={styles.headline}>
                {content.headline}
              </Text>
            )}
            {!!content.subtitle && (
              <Text size="xs" color="secondary" style={styles.subtitle}>
                {content.subtitle}
              </Text>
            )}
          </View>
        </View>
        {isDismissible && (
          <Pressable
            onPress={handleDismiss}
            accessibilityRole="button"
            accessibilityLabel="Dismiss"
            hitSlop={6}
            style={({ hovered, pressed }) => [
              styles.dismissButton,
              Platform.OS === 'web' && hovered && styles.dismissButtonHover,
              pressed && { opacity: 0.85 },
            ]}
          >
            <Icon name="xmark" size={11} color={colors.text_color_tertiary} />
          </Pressable>
        )}
      </View>

      <View style={[
        styles.bottomRow,
        { ...(!hasPromoCode && !hasCountdown ? { gap: 0, width: '100%' } : {}) },
      ]}>
        {(hasPromoCode || hasCountdown) && (
          <View style={styles.metaRow}>
            {hasPromoCode && (
              <Pressable
                onPress={handleCTA}
                accessibilityRole="button"
                accessibilityLabel={`Apply offer, code ${promoCode.code}`}
                style={({ hovered, pressed }) => [
                  styles.promoCodePill,
                  Platform.OS === 'web' && hovered && styles.promoCodePillHover,
                  pressed && { opacity: 0.9 },
                ]}
              >
                <Icon name="tag" size={12} color={accent} />
                <Text size="s" weight="bold" style={{ color: accent }}>
                  {promoCode.code}
                </Text>
              </Pressable>
            )}
            {hasCountdown && (
              <View style={styles.countdownRow}>
                <Icon name="clock" size={11} color={colors.text_color_tertiary} />
                <BannerCountdown
                  endsAt={countdown.endsAt}
                  onExpired={handleCountdownExpired}
                  textColor={colors.text_color_secondary}
                  textSize="xs"
                />
              </View>
            )}
          </View>
        )}

        {hasCTA && (
          <Pressable
            onPress={handleCTA}
            accessibilityRole="button"
            accessibilityLabel={content.ctaText}
            style={({ hovered, pressed }) => [
              styles.ctaBtn,
              !hasPromoCode && !hasCountdown && { flex: 1 },
              Platform.OS === 'web' && hovered && styles.ctaBtnHover,
              pressed && { opacity: 0.88 },
            ]}
          >
            <Text size="xs" weight="bold" style={{ color: accent }}>{content.ctaText}</Text>
            <Icon name="arrow-right" size={10} color={accent} />
          </Pressable>
        )}
      </View>
    </Animated.View>
  );
}

export default React.memo(PromoBanner);
